import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@shared/components/ui/dialog'
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Badge } from '@shared/components/ui/badge'
import { Button } from '@shared/components/ui/button'
import { Separator } from '@shared/components/ui/separator'
import { Skeleton } from '@shared/components/ui/skeleton'
import { toast } from '@shared/hooks/use-toast'
import { apiUrl } from '@/lib/api'
import { SubscriptionStatusBadge } from './SubscriptionStatusBadge'
import { PaymentInfoCard } from './PaymentInfoCard'
import { ActionButtonsSection } from './ActionButtonsSection'
import { ErrorBoundary } from '@shared/components/ErrorBoundary'
import { 
  User, 
  Building, 
  Calendar, 
  CreditCard, 
  Clock, 
  DollarSign,
  RefreshCw,
  AlertCircle,
  Loader2,
  X
} from 'lucide-react'

interface SubscriptionDetailsDialogProps {
  subscription: {
    id: string
    user_email: string
    business_name: string
    plan_name?: string
    plan_monthly_price_cents?: number
    plan_annual_price_cents?: number
    billing_cycle: string
    status: string
    payment_status?: string
    current_period_end?: string
    trial_ends_at?: string
    created_at?: string
    updated_at?: string
  }
  isOpen: boolean
  onClose: () => void
  onSubscriptionUpdated: () => void
}

const LoadingSkeleton = () => (
  <div className="space-y-6">
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-6 w-20" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-24" />
      </div>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-4 w-full" />
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-4 w-full" />
        </CardContent>
      </Card>
    </div>
    
    <div className="space-y-4">
      <Skeleton className="h-6 w-32" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="text-center p-4 bg-muted rounded-lg">
            <Skeleton className="h-8 w-16 mx-auto mb-2" />
            <Skeleton className="h-4 w-20 mx-auto" />
          </div>
        ))}
      </div>
    </div>
  </div>
)

export default function SubscriptionDetailsDialog({
  subscription,
  isOpen,
  onClose,
  onSubscriptionUpdated
}: SubscriptionDetailsDialogProps) {
  const [plans, setPlans] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)


  useEffect(() => {
    if (isOpen) {
      loadPlans()
    }
  }, [isOpen])

  const loadPlans = async (isRetry = false) => {
    if (isRetry) {
      setRetryCount(prev => prev + 1)
    }
    
    setLoading(true)
    setError(null)
    
    try {
      // Get fresh token
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        console.error('No authentication token available')
        setError('Authentication required')
        return
      }

      const requestHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      const response = await fetch(apiUrl('/api/admin/plans'), { headers: requestHeaders })
      if (response.ok) {
        const data = await response.json()
        setPlans(data.plans || [])
        setRetryCount(0)
      } else {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = errorData.error || `Failed to load plans (${response.status})`
        setError(errorMessage)
        
        if (isRetry) {
          toast({
            title: 'Failed to Load Plans',
            description: errorMessage,
            variant: 'destructive'
          })
        }
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Network error occurred'
      setError(errorMessage)
      
      if (isRetry) {
        toast({
          title: 'Connection Error',
          description: 'Unable to connect to the server. Please check your connection and try again.',
          variant: 'destructive'
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleRetry = () => {
    loadPlans(true)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      console.error('Date formatting error:', error, 'for date:', dateString)
      return 'Invalid Date'
    }
  }

  const getSubscriptionAge = () => {
    if (!subscription.created_at) return '—'
    const created = new Date(subscription.created_at)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - created.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays < 30) return `${diffDays} days`
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months`
    return `${Math.floor(diffDays / 365)} years`
  }

  const handleSubscriptionUpdated = () => {
    onSubscriptionUpdated()
    toast({
      title: 'Success',
      description: 'Subscription updated successfully',
    })
  }

  if (!subscription) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-2xl">Subscription Details</DialogTitle>
              <DialogDescription>
                Manage subscription settings and view detailed information
              </DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
              aria-label="Close dialog"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <ErrorBoundary
          onError={(error, errorInfo) => {
            console.error('SubscriptionDetailsDialog error:', error, errorInfo)
            toast({
              title: 'Unexpected Error',
              description: 'An unexpected error occurred. Please try again.',
              variant: 'destructive'
            })
          }}
          onRetry={() => {
            setError(null)
            loadPlans()
          }}
        >
          <div className="space-y-6">
            {/* Subscription Overview */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Subscription Overview
                  </CardTitle>
                  <SubscriptionStatusBadge status={subscription.status} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">User Email</label>
                      <div className="flex items-center gap-2 mt-1">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{subscription.user_email}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Business Name</label>
                      <div className="flex items-center gap-2 mt-1">
                        <Building className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{subscription.business_name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Subscription ID</label>
                      <div className="font-mono text-sm bg-muted px-2 py-1 rounded mt-1">
                        {subscription.id}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Age</label>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>{getSubscriptionAge()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Information */}
            <PaymentInfoCard subscription={subscription} />

            {/* Action Buttons */}
            <ActionButtonsSection
              subscription={subscription}
              plans={plans}
              onSubscriptionUpdated={handleSubscriptionUpdated}
            />

            {/* Error State */}
            {error && (
              <Card className="border-destructive/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-destructive">
                    <AlertCircle className="h-5 w-5" />
                    Error Loading Plans
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">{error}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleRetry} className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4" />
                      Retry ({retryCount}/3)
                    </Button>
                    <Button variant="ghost" onClick={() => setError(null)}>
                      Dismiss
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Loading State */}
            {loading && <LoadingSkeleton />}

            {/* Quick Stats */}
            {!loading && !error && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <DollarSign className="h-5 w-5" />
                    Quick Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        ${subscription.billing_cycle === 'annual' 
                          ? ((subscription.plan_annual_price_cents || 0) / 100).toFixed(0)
                          : ((subscription.plan_monthly_price_cents || 0) / 100).toFixed(0)
                        }
                      </div>
                      <div className="text-sm text-muted-foreground">Monthly Value</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {subscription.billing_cycle === 'annual' ? '12' : '1'}
                      </div>
                      <div className="text-sm text-muted-foreground">Billing Cycles</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {subscription.status === 'active' ? '100%' : '0%'}
                      </div>
                      <div className="text-sm text-muted-foreground">Uptime</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">
                        {subscription.payment_status === 'active' ? '✅' : 
                         subscription.payment_status === 'past_due' ? '⚠️' :
                         subscription.payment_status === 'canceled' ? '❌' :
                         subscription.payment_status === 'cancel_at_period_end' ? '⏰' :
                         '❌'}
                      </div>
                      <div className="text-sm text-muted-foreground">Payment Status</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </ErrorBoundary>
      </DialogContent>
    </Dialog>
  )
}